import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from '../components/ui/dialog';
import { 
    FolderKanban, 
    Plus, 
    Users, 
    Calendar,
    CheckCircle,
    Clock,
    MoreVertical
} from 'lucide-react';

import API_URL from '../utils/api';

const Projects = () => {
    const { user, canManage } = useAuth();
    const [projects, setProjects] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        start_date: '',
        end_date: '',
        manager_id: '',
        team_members: []
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [projectsRes, empRes] = await Promise.all([
                axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
                axios.get(`${API_URL}/api/employees`, { withCredentials: true })
            ]);
            setProjects(projectsRes.data);
            setEmployees(empRes.data.filter(e => e.role !== 'admin'));
        } catch (error) {
            logger.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
                end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
            };
            await axios.post(`${API_URL}/api/projects`, payload, { withCredentials: true });
            setIsDialogOpen(false);
            setFormData({ name: '', code: '', description: '', start_date: '', end_date: '', manager_id: '', team_members: [] });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error creating project');
        }
    };

    const getStatusColor = (project) => {
        if (!project.task_count) return 'bg-slate-100 text-slate-800 border-slate-200';
        const progress = (project.completed_count / project.task_count) * 100;
        if (progress === 100) return 'bg-green-100 text-green-800 border-green-200';
        if (progress > 50) return 'bg-blue-100 text-blue-800 border-blue-200';
        return 'bg-amber-100 text-amber-800 border-amber-200';
    };

    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading projects...</div></div>;
    }

    return (
        <div className="space-y-6" data-testid="projects-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Projects
                    </h1>
                    <p className="text-slate-500 mt-1">Manage projects and track progress</p>
                </div>
                
                {(canManage || user?.role === 'manager') && (
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-brand hover:bg-brand rounded-none" data-testid="add-project-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                New Project
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-none max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    Create Project
                                </DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Create or edit a project with team, timeline and budget.</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Project Name</Label>
                                        <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="rounded-none mt-1" required data-testid="project-name-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Code</Label>
                                        <Input value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} className="rounded-none mt-1" placeholder="PRJ-001" required data-testid="project-code-input" />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
                                    <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="rounded-none mt-1" rows={3} data-testid="project-description-input" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Start Date</Label>
                                        <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} className="rounded-none mt-1" data-testid="project-start-date-input" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">End Date</Label>
                                        <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} className="rounded-none mt-1" data-testid="project-end-date-input" />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" className="rounded-none" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                                    <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="project-save-btn">Create Project</Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project) => (
                    <Card key={project._id} className="rounded-none border-slate-200 hover:border-blue-300 transition-colors" data-testid={`project-card-${project._id}`}>
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-4">
                                <div className="p-3 bg-blue-100">
                                    <FolderKanban className="h-6 w-6 text-brand" />
                                </div>
                                <Badge className={`rounded-none ${getStatusColor(project)}`}>
                                    {project.status || 'active'}
                                </Badge>
                            </div>
                            <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                {project.name}
                            </h3>
                            <p className="text-sm text-slate-500 mt-1">{project.code}</p>
                            <p className="text-slate-600 text-sm mt-3 line-clamp-2">{project.description || 'No description'}</p>
                            
                            <div className="mt-4 pt-4 border-t border-slate-200">
                                <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                        <span>{project.completed_count || 0}/{project.task_count || 0} tasks</span>
                                    </div>
                                    {project.task_count > 0 && (
                                        <span className="font-medium text-brand">
                                            {Math.round((project.completed_count / project.task_count) * 100)}%
                                        </span>
                                    )}
                                </div>
                                {project.task_count > 0 && (
                                    <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-brand transition-all" 
                                            style={{ width: `${(project.completed_count / project.task_count) * 100}%` }}
                                        />
                                    </div>
                                )}
                            </div>

                            {(project.start_date || project.end_date) && (
                                <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
                                    <Calendar className="h-4 w-4" />
                                    <span>
                                        {project.start_date && new Date(project.start_date).toLocaleDateString()}
                                        {project.start_date && project.end_date && ' - '}
                                        {project.end_date && new Date(project.end_date).toLocaleDateString()}
                                    </span>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
                {projects.length === 0 && (
                    <div className="col-span-full text-center py-12 text-slate-500">
                        No projects found. Create one to get started.
                    </div>
                )}
            </div>
        </div>
    );
};

export default Projects;
